import * as React from 'react'
import { DBSchema, IDBPDatabase, openDB } from 'idb'

import { MaybeRenderProp, createContext } from '@clara/react-utils'
import { runIfFn } from '@clara/utils'
import { v4 as uuidv4 } from 'uuid'
import { useEffectOnce } from '@clara/hooks'

const DATABASE_NAME = 'clara-payments-idb'
const OBJECT_STORE = 'payments-from-reimbursements'

const [SharedDbContextProvider, useSharedDbContext] =
  createContext<SharedDbContext>({
    name: 'SharedDbContext',
    strict: true,
  })

export { useSharedDbContext }

interface PaymentsIDBDatabase extends DBSchema {
  [OBJECT_STORE]: {
    key: string
    value: any
  }
}

function useSharedDb() {
  const [db, setDb] = React.useState<IDBPDatabase<PaymentsIDBDatabase>>()

  const init = async () => {
    try {
      const temp = await openDB<PaymentsIDBDatabase>(DATABASE_NAME, 1, {
        upgrade(db) {
          db.createObjectStore(OBJECT_STORE)
        },
      })
      setDb(temp)
    } catch (error) {
      console.error('IDB - Error initializing:', error)
    }
  }

  const set = React.useCallback(
    async (payload: any) => {
      try {
        if (db) {
          const uid = uuidv4()
          await db.put(OBJECT_STORE, payload, uid)
          return uid
        } else {
          throw new Error("IDB isn't initialized")
        }
      } catch (error) {
        console.error('IDB - Error in set:', error)
        return null
      }
    },
    [db],
  )

  const get = React.useCallback(
    async (uuid: string) => {
      try {
        if (db) {
          const res = await db.get(OBJECT_STORE, uuid)
          return res
        } else {
          throw new Error("IDB isn't initialized")
        }
      } catch (error) {
        console.error('IDB - Error in get:', error)
        return null
      }
    },
    [db],
  )

  useEffectOnce(() => {
    init()
  })

  return {
    set,
    get,
  }
}

type SharedDbContext = ReturnType<typeof useSharedDb>

export interface SharedDbProviderProps {
  children?: MaybeRenderProp<SharedDbContext>
}

export function SharedDbProvider(props: SharedDbProviderProps) {
  const ctx = useSharedDb()
  const context = React.useMemo(() => ctx, [ctx])

  const children = runIfFn(props.children, context)

  return (
    <SharedDbContextProvider value={context}>
      {children}
    </SharedDbContextProvider>
  )
}
